import { type ComponentPropsWithRef, type ComponentType, useMemo } from "react";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import {
  CarReservationCard,
  CarReservationDetailPage,
} from "@apac/components/cars";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import type { Itinerary } from "@b2bportal/ground-api";
import { ItineraryState } from "@b2bportal/ground-api/lib/api";
import { Product } from "@b2bportal/purchase-api";
import type { TripProduct } from "@overrides/types";
import { carsPostBookingApi } from "../store";

type CarTripProduct = TripProduct<Itinerary>;

export const useCarTripProduct = ({
  tripsPath,
  reservationPath,
}: {
  tripsPath: string;
  reservationPath: (reservationId: string) => string;
}): {
  cars: { [Product.Ground]: CarTripProduct };
  pastCars: { [Product.Ground]: CarTripProduct };
  carReservationRouteProps: RouteObject;
} => {
  const [fetch, reservations] =
    carsPostBookingApi.endpoints.fetchCarItineraries.useLazyQuery();
  const navigate = useNavigate();

  const product = useMemo(
    () => ({
      fetch,
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<ComponentType<{ reservation: Itinerary }>>) => (
        <CarReservationCard
          itinerary={reservation}
          selectItinerary={() =>
            navigate(
              reservationPath(reservation.reservation.internalGroundBookingId)
            )
          }
          {...props}
        />
      ),
    }),
    [reservationPath, fetch, navigate]
  );

  return useMemo(
    () => ({
      cars: {
        [Product.Ground]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data[ItineraryState.Future] ?? []),
            ...(data[ItineraryState.Present] ?? []),
          ]),
        },
      },
      pastCars: {
        [Product.Ground]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data[ItineraryState.Past] ?? []),
            ...(data[ItineraryState.Canceled] ?? []),
          ]),
        },
      },
      carReservationRouteProps: {
        path: reservationPath(":reservationId"),
        element: <CarReservationDetailPage tripsRoute={tripsPath} />,
      },
    }),
    [product, reservationPath, reservations, tripsPath]
  );
};
